import { useState } from 'react';
import { BASE_URL } from '@/services/api';
import { cn } from '@/lib/utils';

interface UserAvatarProps {
  name: string | null | undefined;
  avatar?: string | null;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  colorClass?: string;
  className?: string;
}

const sizeClasses = {
  xs: 'w-6 h-6 text-caption',
  sm: 'w-7 h-7 text-caption',
  md: 'w-8 h-8 text-body',
  lg: 'w-10 h-10 text-body',
};

function getInitials(name: string | null | undefined): string {
  if (!name?.trim()) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function resolveAvatarUrl(avatar: string): string {
  return avatar.startsWith('http') ? avatar : `${BASE_URL}${avatar}`;
}

export function UserAvatar({ name, avatar, size = 'sm', colorClass, className }: UserAvatarProps) {
  const colors = colorClass ?? 'bg-[var(--bp-surface-3)] text-[var(--bp-text-2-soft)] border border-[var(--bp-border)]';
  const sizeClass = sizeClasses[size];

  /**
   * T1328 (a): ko datoteka avatarja ne obstaja, je do zdaj obviselo <img> na
   * mrtvem naslovu — brskalnik je izrisal ZLOMLJENO sliko namesto začetnic.
   * Izmerjeno: `GET /media/org_2/avatars/sedem.png` → `net::ERR_BLOCKED_BY_ORB`,
   * ker Django vrne 404 s **HTML** telesom, ki ga ORB zavrne kot sliko. Zapis
   * poti torej ni bilo mogoče ločiti od veljavne slike vnaprej — edini
   * zanesljiv signal je `onError`, zato se ob njem vrnemo na isti krogec z
   * inicialkami, kot ga imajo člani brez avatarja.
   */
  // Hrani se NASLOV, ki je padel, ne le zastavica: nov avatar tako dobi novo
  // priložnost brez učinka (`useEffect` + `setState` bi bil kaskadni izris).
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const src = avatar ? resolveAvatarUrl(avatar) : null;

  if (src && failedSrc !== src) {
    return (
      <img
        src={src}
        alt={name ?? ''}
        onError={() => setFailedSrc(src)}
        className={cn('rounded-full object-cover border border-[var(--bp-border)] shrink-0', sizeClass, className)}
      />
    );
  }

  return (
    <div className={cn('rounded-full flex items-center justify-center font-bold shrink-0', sizeClass, colors, className)}>
      {getInitials(name)}
    </div>
  );
}
